import * as XLSX from 'xlsx';
import moment from 'moment';

export interface InvoiceRow {
  client?: string;
  invoiceDate?: string;
  amount?: string | number;
  rate?: string | number;
  currency?: string;
  bank?: string;
  transactionId?: string;
  [key: string]: any;
}

const excelDateToJSDate = (excelDateNum: number): Date => {
  const excelEpoch = new Date(1900, 0, 1);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;

  // Adjust for Excel's leap year bug (1900 is not a leap year)
  const adjustedDays = excelDateNum > 59 ? excelDateNum - 1 : excelDateNum;

  return new Date(excelEpoch.getTime() + (adjustedDays - 1) * millisecondsPerDay);
};

const parseInvoiceDate = (dateValue: any): string => {
  if (!dateValue) return '';

  if (typeof dateValue === 'number') {
    try {
      const jsDate = excelDateToJSDate(dateValue);
      return moment(jsDate).add(1, 'day').format('MM/DD/YYYY');
    } catch (error) {
      console.error('Error converting Excel date:', dateValue, error);
      return '';
    }
  }

  // If it's a string, try to parse it
  if (typeof dateValue === 'string') {
    const trimmedDate = dateValue.trim();

    // Try parsing with moment (handles multiple formats)
    const parsed = moment(trimmedDate, [
      'DD/MM/YYYY',
      'MM/DD/YYYY',
      'YYYY-MM-DD',
      'DD-MM-YYYY',
      'D/M/YYYY',
      'M/D/YYYY',
      'YYYY/MM/DD',
      'DD.MM.YYYY',
      'D.M.YYYY'
    ], true);

    if (parsed.isValid()) {
      return parsed.add(1, 'day').format('MM/DD/YYYY');
    }
  }

  // If it's a Date object
  if (dateValue instanceof Date) {
    return moment(dateValue).add(1, 'day').format('MM/DD/YYYY');
  }

  return '';
};

export const parseExcelFile = (file: File): Promise<InvoiceRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData: InvoiceRow[] = XLSX.utils.sheet_to_json(worksheet);
        console.log('jsonData', jsonData)

        const cleanedData = jsonData.map((row: InvoiceRow) => {
          return {
            client: row.client?.toString().trim() || '',
            invoiceDate: parseInvoiceDate(row.invoiceDate),
            amount: row.amount ? parseFloat(row.amount.toString()) : 0,
            rate: row.rate ? parseFloat(row.rate.toString()) : 1,
            currency: row.currency?.toString().trim() || '',
            bank: row.bank?.toString().trim() || '',
            transactionId: row.transactionId?.toString().trim() || '',
          };
        });

        resolve(cleanedData);
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

export const validateInvoiceData = (data: InvoiceRow[]): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  console.log('data', data)

  data.forEach((row, index) => {
    const rowNum = index + 2;

    if (!row.client) {
      errors.push(`Row ${rowNum}: Client is required`);
    }
    if (!row.invoiceDate) {
      errors.push(`Row ${rowNum}: Invoice Date is required`);
    }
    if (!row.amount || Number(row.amount) < 1) {
      errors.push(`Row ${rowNum}: Amount must be a positive number`);
    }
    if (!row.currency) {
      errors.push(`Row ${rowNum}: Currency is required`);
    }
    if (!row.bank) {
      errors.push(`Row ${rowNum}: Bank is required`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
